'use client'

import { EmblaCarouselType } from 'embla-carousel'
import { assertEvent, assign, createMachine, fromCallback } from 'xstate'

import { GalleryAsset } from '@/data/getEntryGallery'
import { createActorContext } from '@/utils/createActorContext'
import { machineTypes } from '@/utils/machineTypes'

export type ListingGalleryAsset = {
  url: string
  id: string
  kind: 'iframe'
  type: 'iframe'
  thumbnailUrl: string
  alt: string | null
  width: number
  height: number
  caption: string | null
}

export type ImageGalleryAsset = Omit<Extract<GalleryAsset, { kind: 'image' }>, 'blur'> & {
  thumbnailUrl?: string
  blur?: string
}
export type VideoGalleryAsset = Omit<Extract<GalleryAsset, { kind: 'video' }>, 'blur'> & {
  thumbnailUrl?: string
  blur?: string
}
export type CarouselGalleryAsset = ImageGalleryAsset | VideoGalleryAsset | ListingGalleryAsset

const ModalGalleryMachine = createMachine(
  {
    /** @xstate-layout N4IgpgJg5mDOIC5QHECGAbdYBOBPAdAJIB2qAxgC4CWAbmAMQCCltqFYABFBlngNoAGALqJQABwD2sKtQnFRIAB6IArAPwB2AMwCALADYAHBpUAaELkQBGAEwBOfCptbdWkwF935tJhwES5NR0TCw0bJzcvvxWIkggktKy8nHKCIb6+FYCdm4CNoaGNioqVmYWiDYG+PkaejYmBvoahZ7ePH5EpKEMzEHhXO38NrHiUjJUcgqplVaZhrpqzVpaKumG5pYIWobquib6dsZ2KssCKq0gPrz+XUEMAMpgFByMAAqEsIIj8WNJU4gAWisGg0+CMJg21nm+EKhls5y8l0GBF6tAYABEwIFWOwBlFcF8FAlxpMUohoVZ9AJSvoipCtupgXZwQi2vj8KjgsgJBwKDyxNgwDQJgBXWAcGRgAC2hLixL+ZIQpRU+C0+i0+Xh9KBAgydl0dhsVmMrKR7M5DG5vJ5xDAimekplwiJvwmyVAqScKo0huZJu1WT1BqNJouVw6FvoVr5EvYTu+8rd-wQVPUdgEhjccKyOxU+npwO9JzUrhyGgW6U8iOIEggcAU4bwLsSScVAJsRTB-vKCCB+vwumDzlzuuOhjDyM62LozZJ7qUgMK+GpwONEJ7xlVxQMocRjZR3VnCo9iAMsxXGjXZU2xrB+XTtIRniAA */
    id: 'Gallery',
    types: machineTypes<{
      context: {
        index: number
        items: CarouselGalleryAsset[]
        caption: string | null
        limit: number | null
        galleryApi: EmblaCarouselType | undefined
        thumbnailsApi: EmblaCarouselType | undefined
      }
      input: Partial<{
        index: number
        items: CarouselGalleryAsset[]
        caption: string | null
        limit: number | null
        galleryApi: EmblaCarouselType | undefined
        thumbnailsApi: EmblaCarouselType | undefined
      }>
      events:
        | { type: 'Set APIs'; galleryApi: EmblaCarouselType; thumbnailsApi: EmblaCarouselType }
        | { type: 'Activate gallery'; index?: number }
        | { type: 'Deactivate gallery' }
        | { type: 'Go to previous item' }
        | { type: 'Go to next item' }
        | { type: 'Go to item'; index: number }
    }>(),
    context: ({ input }) => ({
      index: 0,
      items: [],
      caption: '',
      limit: 3,
      galleryApi: undefined,
      thumbnailsApi: undefined,
      ...input,
    }),
    initial: 'Inactive',
    states: {
      Inactive: {
        on: {
          'Activate gallery': [
            {
              target: 'Inactive',
              guard: 'Missing API',
              actions: 'Log missing API error',
            },
            {
              target: 'Active',
              guard: 'Index is valid',
              actions: 'Set index',
            },
            'Active',
          ],

          'Set APIs': {
            target: 'Inactive',
            actions: 'Set APIs',
          },
        },
      },

      Active: {
        on: {
          'Deactivate gallery': 'Inactive',

          'Go to previous item': {
            target: 'Active',
            actions: 'Decrement index',
            guard: 'Not at start',
          },

          'Go to next item': {
            target: 'Active',
            actions: 'Increment index',
            guard: 'Not at end',
          },

          'Go to item': {
            target: 'Active',
            guard: 'Index is valid',
            actions: 'Set index',
          },
        },

        invoke: [
          {
            src: 'Listen for escape key',
            id: 'escapeKeyListener',
          },
          {
            src: 'Listen for arrow keys',
            id: 'arrowKeyListener',
          },
        ],

        exit: 'Stop Videos',
      },
    },
  },
  {
    guards: {
      'Missing API': ({ context }) => !context.galleryApi || !context.thumbnailsApi,
      'Not at start': ({ context }) => context.index > 0,
      'Not at end': ({ context }) => context.index < context.items.length - 1,
      'Index is valid': ({ context, event }) =>
        'index' in event && typeof event.index === 'number' && event.index >= 0 && event.index < context.items.length,
    },
    actions: {
      'Set APIs': assign(({ event }) => {
        assertEvent(event, 'Set APIs')

        return {
          galleryApi: event.galleryApi,
          thumbnailsApi: event.thumbnailsApi,
        }
      }),
      'Set index': assign(({ context, event }) => {
        const index = 'index' in event && typeof event.index === 'number' ? event.index : context.index
        context.galleryApi?.scrollTo(index)
        context.thumbnailsApi?.scrollTo(index)
        return { index }
      }),
      'Increment index': assign(({ context }) => {
        context.galleryApi?.scrollNext()
        context.thumbnailsApi?.scrollNext()
        return {
          index: Math.min(context.index + 1, context.items.length - 1),
        }
      }),
      'Decrement index': assign(({ context }) => {
        context.galleryApi?.scrollPrev()
        context.thumbnailsApi?.scrollPrev()
        return { index: Math.max(context.index - 1, 0) }
      }),
      'Log missing API error': ({ context }) => {
        if (!context.galleryApi) {
          console.error('Missing main API')
        }

        if (!context.thumbnailsApi) {
          console.error('Missing thumbnails API')
        }
      },
      'Stop Videos': () => {
        // Kill any Youtube clips
        document.querySelectorAll<HTMLIFrameElement>('.youtube-player').forEach((iframe) => {
          // console.log('✋', iframe)
          iframe.contentWindow?.postMessage(JSON.stringify({ event: 'command', func: 'stopVideo', args: '' }), '*')
        })
      },
    },
    actors: {
      'Listen for escape key': fromCallback(({ sendBack }) => {
        const handleKeyDown = (event: KeyboardEvent) => {
          if (event.key === 'Escape') {
            sendBack({ type: 'Deactivate gallery' })
          }
        }

        document.addEventListener('keydown', handleKeyDown)

        return () => {
          document.removeEventListener('keydown', handleKeyDown)
        }
      }),
      'Listen for arrow keys': fromCallback(({ sendBack }) => {
        const handleKeyDown = (event: KeyboardEvent) => {
          if (event.key === 'ArrowLeft') {
            sendBack({ type: 'Go to previous item' })
          } else if (event.key === 'ArrowRight') {
            sendBack({ type: 'Go to next item' })
          }
        }

        document.addEventListener('keydown', handleKeyDown)

        return () => {
          document.removeEventListener('keydown', handleKeyDown)
        }
      }),
    },
  },
)

export const ModalGalleryContext = createActorContext(ModalGalleryMachine)

export const ModalGalleryProvider = ModalGalleryContext.Provider
